import { useEffect, useMemo, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { useNavigate } from 'react-router-dom';
import WebScrollView from '../../components/WebScrollView';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  RefreshCw,
} from '../../lib/icons';
import { shadows } from '../../lib/styles';
import { supabase } from '../../lib/supabase';

type ServiceRequestStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';
type ServiceRequestPriority = 'low' | 'normal' | 'high' | 'urgent';
type AdminTaskStatus = 'pending' | 'in_progress' | 'completed';
type AdminTaskPriority = 'low' | 'normal' | 'high' | 'urgent';

interface ServiceRequestReportItem {
  id: string;
  title: string;
  priority: ServiceRequestPriority;
  status: ServiceRequestStatus;
  created_at: string;
  owner_id: string | null;
  assigned_owner?: {
    id: string;
    full_name: string | null;
  } | null;
  home?: {
    address: string | null;
    city: string | null;
    state: string | null;
    owner?: {
      full_name: string | null;
    } | null;
  } | null;
}

interface AdminTaskReportItem {
  id: string;
  title: string;
  status: AdminTaskStatus;
  priority: AdminTaskPriority;
  due_date: string | null;
  service_request_id: string | null;
  assignee?: {
    full_name: string | null;
  } | null;
  service_request?: ServiceRequestReportItem | null;
}

interface StandardsReportData {
  normalPriorityRequests: ServiceRequestReportItem[];
  overdueAdminTasks: AdminTaskReportItem[];
  requestsWithoutOpenAdminTask: ServiceRequestReportItem[];
}

interface SystemManagerOption {
  id: string;
  full_name: string | null;
}

const OPEN_SERVICE_REQUEST_STATUSES: ServiceRequestStatus[] = ['pending', 'in_progress'];
const OPEN_ADMIN_TASK_STATUSES: AdminTaskStatus[] = ['pending', 'in_progress'];
const MISSING_TASKS_UNASSIGNED_OWNER = '__unassigned__';

const COLORS = {
  primary: '#000000',
  secondary: '#4b5563',
  muted: '#9ca3af',
  border: '#e5e7eb',
  danger: '#dc2626',
  warning: '#ea580c',
  success: '#16a34a',
  info: '#2563eb',
};

const requestSelect = `
  id,
  title,
  priority,
  status,
  created_at,
  owner_id,
  assigned_owner:profiles!owner_id (
    id,
    full_name
  ),
  home:homes (
    address,
    city,
    state,
    owner:profiles!homes_owner_id_fkey (
      full_name
    )
  )
`;

const normalizeRelated = <T,>(value: T | T[] | null | undefined): T | null => {
  if (Array.isArray(value)) return value[0] || null;
  return value || null;
};

const formatStatus = (status: string) =>
  status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatDate = (date: string | null) => {
  if (!date) return 'No date';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getRequestLocation = (request: ServiceRequestReportItem) => {
  const home = normalizeRelated(request.home);
  if (!home) return 'No home linked';
  const location = [home.address, home.city, home.state].filter(Boolean).join(', ');
  return location || 'No address';
};

const getHomeownerName = (request: ServiceRequestReportItem) => {
  const home = normalizeRelated(request.home);
  const owner = normalizeRelated(home?.owner);
  return owner?.full_name || 'Unknown homeowner';
};

const normalizeRequest = (request: any): ServiceRequestReportItem => ({
  ...request,
  home: normalizeRelated(request.home),
  assigned_owner: normalizeRelated(request.assigned_owner),
});

const normalizeAdminTask = (task: any): AdminTaskReportItem => ({
  ...task,
  assignee: normalizeRelated(task.assignee),
  service_request: task.service_request ? normalizeRequest(normalizeRelated(task.service_request)) : null,
});

const ReportsStandards = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StandardsReportData>({
    normalPriorityRequests: [],
    overdueAdminTasks: [],
    requestsWithoutOpenAdminTask: [],
  });
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [missingTasksPriorityFilter, setMissingTasksPriorityFilter] = useState<
    'all' | ServiceRequestPriority
  >('all');
  const [missingTasksOwnerFilter, setMissingTasksOwnerFilter] = useState<string>('all');
  const [systemManagers, setSystemManagers] = useState<SystemManagerOption[]>([]);

  useEffect(() => {
    fetchStandardsReport();
  }, []);

  const fetchStandardsReport = async () => {
    setError(null);
    setRefreshing(true);
    try {
      const now = new Date().toISOString();

      const [
        normalPriorityResult,
        openRequestsResult,
        openAdminTasksResult,
        overdueTasksResult,
        managersResult,
      ] = await Promise.all([
        supabase
          .from('service_requests')
          .select(requestSelect)
          .eq('priority', 'normal')
          .in('status', OPEN_SERVICE_REQUEST_STATUSES)
          .order('created_at', { ascending: false }),
        supabase
          .from('service_requests')
          .select(requestSelect)
          .in('status', OPEN_SERVICE_REQUEST_STATUSES)
          .order('created_at', { ascending: false }),
        supabase
          .from('admin_tasks')
          .select('id, service_request_id')
          .in('status', OPEN_ADMIN_TASK_STATUSES)
          .not('service_request_id', 'is', null),
        supabase
          .from('admin_tasks')
          .select(`
              id,
              title,
              status,
              priority,
              due_date,
              service_request_id,
              assignee:profiles!assigned_to ( full_name ),
              service_request:service_requests!service_request_id (
                ${requestSelect}
              )
            `)
          .in('status', OPEN_ADMIN_TASK_STATUSES)
          .not('service_request_id', 'is', null)
          .not('due_date', 'is', null)
          .lt('due_date', now)
          .order('due_date', { ascending: true }),
        supabase
          .from('profiles')
          .select('id, full_name')
          .eq('is_system_manager', true)
          .order('full_name', { ascending: true }),
      ]);

      if (normalPriorityResult.error) throw normalPriorityResult.error;
      if (openRequestsResult.error) throw openRequestsResult.error;
      if (openAdminTasksResult.error) throw openAdminTasksResult.error;
      if (overdueTasksResult.error) throw overdueTasksResult.error;
      if (managersResult.error) throw managersResult.error;

      setSystemManagers((managersResult.data || []) as SystemManagerOption[]);

      const openAdminTaskRequestIds = new Set(
        (openAdminTasksResult.data || [])
          .map((task: any) => task.service_request_id)
          .filter(Boolean)
      );

      const openRequests = (openRequestsResult.data || []).map(normalizeRequest);

      setData({
        normalPriorityRequests: (normalPriorityResult.data || []).map(normalizeRequest),
        overdueAdminTasks: (overdueTasksResult.data || []).map(normalizeAdminTask),
        requestsWithoutOpenAdminTask: openRequests.filter(
          (request) => !openAdminTaskRequestIds.has(request.id)
        ),
      });
    } catch (err: any) {
      console.error('Error fetching standards report:', err);
      setError(err?.message || 'Failed to load standards report');
      setData({
        normalPriorityRequests: [],
        overdueAdminTasks: [],
        requestsWithoutOpenAdminTask: [],
      });
      setSystemManagers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const totals = useMemo(
    () => ({
      normalPriority: data.normalPriorityRequests.length,
      overdueTasks: data.overdueAdminTasks.length,
      missingTasks: data.requestsWithoutOpenAdminTask.length,
    }),
    [data]
  );

  const filteredMissingAdminTasks = useMemo(() => {
    let list = data.requestsWithoutOpenAdminTask;
    if (missingTasksPriorityFilter !== 'all') {
      list = list.filter((request) => request.priority === missingTasksPriorityFilter);
    }
    if (missingTasksOwnerFilter === MISSING_TASKS_UNASSIGNED_OWNER) {
      list = list.filter((request) => !normalizeRelated(request.assigned_owner)?.id);
    } else if (missingTasksOwnerFilter !== 'all') {
      list = list.filter(
        (request) => normalizeRelated(request.assigned_owner)?.id === missingTasksOwnerFilter
      );
    }
    return list;
  }, [
    data.requestsWithoutOpenAdminTask,
    missingTasksPriorityFilter,
    missingTasksOwnerFilter,
  ]);

  const missingTasksFiltersActive =
    missingTasksPriorityFilter !== 'all' || missingTasksOwnerFilter !== 'all';

  const handleRequestClick = (
    event: MouseEvent<HTMLAnchorElement>,
    requestId: string
  ) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    navigate(`/admin/service-request/${requestId}`);
  };

  const renderRequestLink = (
    request: ServiceRequestReportItem,
    children: ReactNode,
    style?: any
  ) => {
    const href = `/admin/service-request/${request.id}`;
    if (Platform.OS === 'web') {
      return (
        <a
          href={href}
          style={style as any}
          onClick={(event) => handleRequestClick(event, request.id)}
        >
          {children}
        </a>
      );
    }

    return (
      <Pressable style={style} onPress={() => navigate(href)}>
        {children}
      </Pressable>
    );
  };

  const renderRequestRow = (request: ServiceRequestReportItem, detail?: string) => (
    <View key={request.id} style={styles.listRow}>
      {renderRequestLink(
        request,
        <>
          <View style={styles.itemTitleRow}>
            <Text style={styles.itemTitle}>{request.title || 'Untitled request'}</Text>
            <ExternalLink size={14} color={COLORS.info} />
          </View>
          <Text style={styles.itemMeta}>
            {getHomeownerName(request)} - {getRequestLocation(request)}
          </Text>
          <View style={styles.badgesRow}>
            <View style={[styles.badge, styles.badgeNeutral]}>
              <Text style={styles.badgeText}>{formatStatus(request.status)}</Text>
            </View>
            <View style={[styles.badge, styles.badgeWarning]}>
              <Text style={styles.badgeText}>{formatStatus(request.priority)} priority</Text>
            </View>
            <Text style={styles.itemDate}>Created {formatDate(request.created_at)}</Text>
          </View>
          {detail ? <Text style={styles.itemDetail}>{detail}</Text> : null}
        </>,
        styles.requestLink
      )}
    </View>
  );

  const renderEmptyState = (message: string) => (
    <View style={styles.emptyContainer}>
      <CheckCircle2 size={20} color={COLORS.success} />
      <Text style={styles.emptyText}>{message}</Text>
    </View>
  );

  const renderSection = (
    sectionKey: string,
    title: string,
    subtitle: string,
    count: number,
    emptyMessage: string,
    children: ReactNode
  ) => {
    const isExpanded = expandedSections[sectionKey] === true;

    return (
      <View style={styles.sectionWidget}>
        <Pressable
          style={styles.sectionHeaderButton}
          onPress={() =>
            setExpandedSections((prev) => ({
              ...prev,
              [sectionKey]: !isExpanded,
            }))
          }
        >
          <View style={styles.sectionHeaderLeft}>
            {isExpanded ? (
              <ChevronDown size={18} color={COLORS.secondary} />
            ) : (
              <ChevronRight size={18} color={COLORS.secondary} />
            )}
            <View style={styles.sectionHeaderText}>
              <View style={styles.sectionTitleRow}>
                <Text style={styles.cardTitle}>{title}</Text>
                <View style={styles.countBadge}>
                  <Text style={styles.countBadgeText}>{count}</Text>
                </View>
              </View>
              <Text style={styles.cardSubtitle}>{subtitle}</Text>
            </View>
          </View>
        </Pressable>
        {isExpanded && (
          <View style={styles.sectionBody}>
            {count === 0 ? renderEmptyState(emptyMessage) : children}
          </View>
        )}
      </View>
    );
  };

  const renderMissingAdminTasksSection = () => {
    const sectionKey = 'missingAdminTasks';
    const isExpanded = expandedSections[sectionKey] === true;
    const baseCount = data.requestsWithoutOpenAdminTask.length;
    const filteredCount = filteredMissingAdminTasks.length;

    const emptyMessage =
      baseCount === 0
        ? 'Every open service request has an open admin task.'
        : filteredCount === 0
          ? 'No requests match these filters.'
          : '';

    return (
      <View style={styles.sectionWidget}>
        <Pressable
          style={styles.sectionHeaderButton}
          onPress={() =>
            setExpandedSections((prev) => ({
              ...prev,
              [sectionKey]: !isExpanded,
            }))
          }
        >
          <View style={styles.sectionHeaderLeft}>
            {isExpanded ? (
              <ChevronDown size={18} color={COLORS.secondary} />
            ) : (
              <ChevronRight size={18} color={COLORS.secondary} />
            )}
            <View style={styles.sectionHeaderText}>
              <View style={styles.sectionTitleRow}>
                <Text style={styles.cardTitle}>
                  Open Service Requests with No Open Admin Task
                </Text>
                <View style={styles.countBadge}>
                  <Text style={styles.countBadgeText}>{filteredCount}</Text>
                </View>
              </View>
              <Text style={styles.cardSubtitle}>
                Pending or in-progress service requests that do not have a pending or in-progress
                admin task.
                {missingTasksFiltersActive && baseCount > 0
                  ? ` Showing ${filteredCount} of ${baseCount}.`
                  : ''}
              </Text>
            </View>
          </View>
        </Pressable>
        {isExpanded && (
          <View style={styles.sectionBody}>
            <View style={styles.filterRow}>
              <View style={styles.filterGroup}>
                <Text style={styles.filterLabel}>Priority</Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={missingTasksPriorityFilter}
                    onValueChange={(value) =>
                      setMissingTasksPriorityFilter(value as 'all' | ServiceRequestPriority)
                    }
                    style={styles.filterPicker}
                  >
                    <Picker.Item label="All priorities" value="all" />
                    <Picker.Item label="Low" value="low" />
                    <Picker.Item label="Normal" value="normal" />
                    <Picker.Item label="High" value="high" />
                    <Picker.Item label="Urgent" value="urgent" />
                  </Picker>
                </View>
              </View>
              <View style={styles.filterGroup}>
                <Text style={styles.filterLabel}>Request owner (system managers)</Text>
                <View style={styles.pickerContainer}>
                  <Picker
                    selectedValue={missingTasksOwnerFilter}
                    onValueChange={(value) => setMissingTasksOwnerFilter(value)}
                    style={styles.filterPicker}
                  >
                    <Picker.Item label="All owners" value="all" />
                    <Picker.Item label="Unassigned" value={MISSING_TASKS_UNASSIGNED_OWNER} />
                    {systemManagers.map((manager) => (
                      <Picker.Item
                        key={manager.id}
                        label={manager.full_name || 'Unnamed'}
                        value={manager.id}
                      />
                    ))}
                  </Picker>
                </View>
              </View>
            </View>
            {missingTasksFiltersActive && (
              <Pressable
                style={styles.clearFiltersButton}
                onPress={() => {
                  setMissingTasksPriorityFilter('all');
                  setMissingTasksOwnerFilter('all');
                }}
              >
                <Text style={styles.clearFiltersText}>Clear filters</Text>
              </Pressable>
            )}
            {baseCount === 0 || filteredCount === 0 ? (
              renderEmptyState(emptyMessage)
            ) : (
              filteredMissingAdminTasks.map((request) => renderRequestRow(request))
            )}
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading standards report...</Text>
      </View>
    );
  }

  return (
    <WebScrollView style={styles.container}>
      <View style={styles.content}>
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <ClipboardList size={24} color={COLORS.primary} />
            <View>
              <Text style={styles.title}>Standards & Quality Assurance</Text>
              <Text style={styles.subtitle}>
                Simple exception lists for internal service request standards.
              </Text>
            </View>
          </View>
          <Pressable
            style={[styles.refreshButton, refreshing && styles.refreshButtonDisabled]}
            onPress={fetchStandardsReport}
            disabled={refreshing}
          >
            {refreshing ? (
              <ActivityIndicator size="small" color={COLORS.secondary} />
            ) : (
              <RefreshCw size={16} color={COLORS.secondary} />
            )}
            <Text style={styles.refreshButtonText}>Refresh</Text>
          </Pressable>
        </View>

        {error && (
          <View style={styles.errorContainer}>
            <AlertTriangle size={18} color="#b91c1c" />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        <View style={styles.statsRow}>
          <View style={[styles.statCard, { borderLeftColor: COLORS.warning }]}>
            <Text style={styles.statLabel}>Normal Priority</Text>
            <Text style={styles.statValue}>{totals.normalPriority}</Text>
          </View>
          <View style={[styles.statCard, { borderLeftColor: COLORS.danger }]}>
            <Text style={styles.statLabel}>Overdue Admin Tasks</Text>
            <Text style={styles.statValue}>{totals.overdueTasks}</Text>
          </View>
          <View style={[styles.statCard, { borderLeftColor: COLORS.info }]}>
            <Text style={styles.statLabel}>No Open Admin Task</Text>
            <Text style={styles.statValue}>{totals.missingTasks}</Text>
          </View>
        </View>

        {renderSection(
          'normalPriority',
          'Service Requests Marked Normal Priority',
          'Open service requests that still need priority triage.',
          data.normalPriorityRequests.length,
          'No open normal-priority service requests.',
          data.normalPriorityRequests.map((request) => renderRequestRow(request))
        )}

        {renderSection(
          'overdueAdminTasks',
          'Service Requests with Overdue Admin Tasks',
          'Open admin tasks linked to service requests with a due date before today.',
          data.overdueAdminTasks.length,
          'No overdue service request admin tasks.',
          data.overdueAdminTasks.map((task) => {
            const request = normalizeRelated(task.service_request);
            if (!request) return null;
            const assigneeName = normalizeRelated(task.assignee)?.full_name || 'Unassigned';
            return renderRequestRow(
              request,
              `${task.title} - Due ${formatDate(task.due_date)} - ${assigneeName}`
            );
          })
        )}

        {renderMissingAdminTasksSection()}
      </View>
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    gap: 24,
    paddingBottom: 24,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
    padding: 32,
  },
  loadingText: {
    fontSize: 16,
    color: COLORS.secondary,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 1,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: COLORS.primary,
  },
  subtitle: {
    fontSize: 13,
    color: COLORS.secondary,
    marginTop: 2,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: '#ffffff',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  refreshButtonDisabled: {
    opacity: 0.6,
  },
  refreshButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: COLORS.secondary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  errorText: {
    color: '#b91c1c',
    fontSize: 14,
    flex: 1,
  },
  statsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  statCard: {
    flex: 1,
    minWidth: 190,
    padding: 16,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#f3f4f6',
    borderLeftWidth: 4,
    gap: 6,
    ...shadows.sm,
  },
  statLabel: {
    fontSize: 13,
    color: COLORS.secondary,
    fontWeight: '500',
  },
  statValue: {
    fontSize: 28,
    fontWeight: '700',
    color: COLORS.primary,
  },
  sectionWidget: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    overflow: 'hidden',
  },
  sectionHeaderButton: {
    padding: 16,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  sectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  sectionHeaderText: {
    flex: 1,
    gap: 6,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  sectionBody: {
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  countBadge: {
    minWidth: 24,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
  },
  countBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: COLORS.primary,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.primary,
  },
  cardSubtitle: {
    fontSize: 13,
    color: COLORS.secondary,
  },
  listRow: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    overflow: 'hidden',
  },
  requestLink: {
    display: 'flex',
    gap: 8,
    padding: 14,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  itemTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  itemTitle: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: COLORS.primary,
  },
  itemMeta: {
    fontSize: 13,
    color: COLORS.secondary,
  },
  badgesRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  badge: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  badgeNeutral: {
    backgroundColor: '#f3f4f6',
  },
  badgeWarning: {
    backgroundColor: '#fff7ed',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#374151',
  },
  itemDate: {
    fontSize: 12,
    color: COLORS.muted,
  },
  itemDetail: {
    fontSize: 13,
    color: COLORS.danger,
    fontWeight: '500',
  },
  emptyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 16,
  },
  emptyText: {
    fontSize: 14,
    color: COLORS.secondary,
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  filterGroup: {
    flex: 1,
    minWidth: 160,
    gap: 4,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: COLORS.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: COLORS.border,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    width: '100%',
  },
  filterPicker: {
    height: Platform.OS === 'ios' ? 200 : 48,
    width: '100%',
  },
  clearFiltersButton: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: COLORS.border,
    backgroundColor: '#f9fafb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer' as any,
    }),
  },
  clearFiltersText: {
    fontSize: 13,
    fontWeight: '500',
    color: COLORS.secondary,
  },
});

export default ReportsStandards;
